import type { SpecsModel } from "./specsModel/SpecsModel";

/**
 * Types for the Specs Generator — output-image-centric model.
 *
 * One input file (with parsed PSD layers) → multiple output images,
 * each derived from a source layer + applied transformations.
 */

// ── Image adjustment / calibration ──────────────────────────────────

export interface LevelsCurve {
  shadows: number; // 0-255
  midtones: number; // 0.01-9.99 (gamma)
  highlights: number; // 0-255
}

export interface ImageAdjustments {
  brightness: number; // -100 to 100
  contrast: number; // -100 to 100
  levels: LevelsCurve;
}

export const DEFAULT_LEVELS: LevelsCurve = {
  shadows: 0,
  midtones: 1.0,
  highlights: 255,
};

export const DEFAULT_ADJUSTMENTS: ImageAdjustments = {
  brightness: 0,
  contrast: 0,
  levels: { ...DEFAULT_LEVELS },
};

// ── Input (uploaded file parsed into layers) ────────────────────────

export interface InputLayer {
  id: string;
  name: string;
  /** Optional authored group hierarchy from layered source files */
  groupPath?: string[];
  /** Full-size data URL of this layer */
  imageDataUrl: string;
  visible: boolean;
  width: number;
  height: number;
}

export interface SpecsInput {
  fileName: string;
  sourceMode?: "single-file" | "structured-file" | "multi-file";
  width: number;
  height: number;
  /** Composite preview of all layers */
  compositeDataUrl: string;
  layers: InputLayer[];
}

// ── Transformations (manual / local only) ───────────────────────────

export type TransformationType =
  | "adjustContrast"
  | "adjustSaturation"
  | "adjustBrightness"
  | "sharpen"
  | "invertColors"
  | "autoMask"
  | "alphaToMask"
  | "whiteToMask"
  | "blackToMask";

export const TRANSFORMATION_LABELS: Record<TransformationType, string> = {
  adjustContrast: "Adjust contrast",
  adjustSaturation: "Adjust saturation",
  adjustBrightness: "Adjust brightness",
  sharpen: "Sharpen",
  invertColors: "Invert colors",
  autoMask: "Auto mask",
  alphaToMask: "Alpha to mask",
  whiteToMask: "White to mask",
  blackToMask: "Black to mask",
};

export const MANUAL_TRANSFORMATIONS: TransformationType[] = [
  "adjustSaturation",
  "adjustContrast",
  "adjustBrightness",
  "sharpen",
  "invertColors",
  "autoMask",
  "alphaToMask",
  "whiteToMask",
  "blackToMask",
];

/** Transformations added by default to new output images */
export const DEFAULT_TRANSFORMATIONS: TransformationType[] = [];

export enum SpecsImageProcessingStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  DONE = "done",
  ERROR = "error",
}

export interface Transformation {
  id: string;
  type: TransformationType;
  enabled: boolean;
  /** Type-specific params (e.g. contrast: 50) */
  params: Record<string, number | string>;
  status: SpecsImageProcessingStatus;
  errorMessage?: string;
  resultImageUrl?: string;
}

// ── AI Transform (single prompt-based flow per output image) ────────

export interface AiGeneration {
  id: string;
  prompt: string;
  imageUrl: string;
  createdAt: string;
}

export type AiMethod = "gpt-image-2" | "sam3";

export interface AiTransform {
  prompt: string;
  /** Which AI backend to use */
  method: AiMethod;
  status: SpecsImageProcessingStatus;
  errorMessage?: string;
  /** All previous generations for this image */
  generations: AiGeneration[];
  /** Accepted result that feeds into the manual filter pipeline */
  acceptedImageUrl: string | null;
}

export const AI_PROMPT_SUGGESTIONS: { label: string; prompt: string }[] = [
  {
    label: "Highlight Global",
    prompt:
      "Change the color to white, remove labels and laces, improve the body texture. Don't alter garment position, keep placement as-is.",
  },
  {
    label: "Mask Global",
    prompt:
      "Turn the highlight image into a black-and-white mask. Don't alter garment position, keep placement as-is.",
  },
  {
    label: "Highlight Laces",
    prompt:
      "Only keep the laces, change the color to white. Don't alter garment position, keep placement as-is.",
  },
  {
    label: "Mask Label",
    prompt:
      "Only keep the back interior behind the neck label, and turn the highlight image into a black-and-white mask. Don't alter garment position, keep placement as-is.",
  },
];

// ── Output Image ────────────────────────────────────────────────────

export const OutputImageRole = {
  None: "none",
  Mask: "mask",
  Highlight: "highlight",
} as const;
export type OutputImageRole =
  (typeof OutputImageRole)[keyof typeof OutputImageRole];
export type SpecsLayerView = "highlight" | "mask" | "preview" | "global";
export enum SpecsPageView {
  Editor = "editor",
  History = "history",
}
export type SpecsDisplayMethod =
  | "colorPicker"
  | "colorPalette"
  | "icon"
  | "none";

export type GlobalOutputImageId =
  | "globalHighlightFront"
  | "globalHighlightBack"
  | "globalMaskFront"
  | "globalMaskBack";

export const OUTPUT_IMAGE_ROLES: { value: OutputImageRole; label: string }[] = [
  { value: OutputImageRole.None, label: "None" },
  { value: OutputImageRole.Mask, label: "Mask" },
  { value: OutputImageRole.Highlight, label: "Highlight" },
];

export interface OutputImageSideState {
  sourceLayerId: string | null;
  referenceImageIds: string[];
  transformations: Transformation[];
  aiTransform: AiTransform;
  outputImageUrl: string | null;
}

/** @deprecated Use OutputImageSideState keyed by OutputImage.sides. */
export type OutputImageBack = OutputImageSideState;

export interface OutputImage extends OutputImageSideState {
  id: string;
  name: string;
  /** Role in the composite preview */
  role: OutputImageRole;
  /** Optional flat background color (null = transparent) */
  backgroundColor: string | null;
  /** Side-keyed image editing state, keyed by generator side name. */
  sides?: Record<string, OutputImageSideState>;
  /** @deprecated Use sides.back. Kept only as a runtime mirror during refactor. */
  back: OutputImageBack | null;
}

// ── Layer (highlight + mask pair) ────────────────────────────────────

export interface SpecsLayerSideIndexes {
  maskFrontSideIndex?: number;
  maskBackSideIndex?: number;
  highlightFrontSideIndex?: number;
  highlightBackSideIndex?: number;
}

export interface SpecsLayerModelPath {
  partName: string;
  elementName: string;
  styleName: string;
  layerName?: string;
}

export type SpecsLayerSideIndexesPatch = Partial<SpecsLayerSideIndexes>;
export type SpecsLayerRuntimeKey = string;

export interface SpecsLayer {
  id: string;
  name: string;
  /** Whether this layer is visible in the composite preview */
  visible: boolean;
  /** Per-layer base color for composite preview */
  baseColor: string;
  /** Stacking order for the compositor and export (higher = on top) */
  zIndex: number;
  /** Stable reference back to the authored model layer when one exists */
  modelLayerPath?: SpecsLayerModelPath | null;
  highlight: OutputImage | null;
  mask: OutputImage | null;
}

export interface SpecsLayerRuntimeState {
  /** Canonical model path key, not a generated session id. */
  key: SpecsLayerRuntimeKey;
  /** Alias kept for UI actions while migrating callers to key. */
  id: SpecsLayerRuntimeKey;
  modelLayerPath: SpecsLayerModelPath;
  visible: boolean;
  baseColor: string;
  zIndex: number;
  /** Runtime side-index metadata for export back to authored generator sides */
  sideIndexes: SpecsLayerSideIndexesPatch;
  highlight: OutputImage | null;
  mask: OutputImage;
}

export type ImageSide = string;

export type SpecsEditorIconPickerTarget =
  | { kind: "element"; partName?: string; elementName: string }
  | { kind: "style"; partName?: string; elementName: string; styleName: string }
  | null;

export type RawVersionMetadata = Record<string, any>;

export const SPECULOOS_NAV_KIND = {
  EMPTY: "empty",
  LAYER: "layer",
  ELEMENT: "element",
  POSITIONS: "positions",
  PARTS: "parts",
  ELEMENTS: "elements",
  STYLES: "styles",
  LAYER_SPECS: "layerSpecs",
  SHIPPING: "shipping",
  METADATA: "metadata",
  GLOBAL: "global",
  FAMILIES: "families",
  FABRICS: "fabrics",
  DECORATIONS: "decorations",
  IMPORT: "import",
  SETTINGS: "settings",
  HISTORY: "history",
  FAMILIES_HISTORY: "familiesHistory",
  FABRICS_HISTORY: "fabricsHistory",
  DECORATIONS_HISTORY: "decorationsHistory",
} as const;

export type SpecsEditorNav =
  | {
      kind: "empty";
      product?: string;
      version?: string;
    }
  | {
      kind: "layer";
      product: string;
      version: string;
      part: string;
      element: string;
      style: string;
      layerId: string;
    }
  | {
      kind: "element";
      product: string;
      version: string;
      part: string;
      element: string;
      style: string;
    }
  | {
      kind: "positions";
      product: string;
      version: string;
      positionId?: string;
      part?: string;
      element?: string;
      style?: string;
      layerId?: string;
    }
  | {
      kind: "parts";
      product: string;
      version: string;
      part?: string;
    }
  | {
      kind: "elements";
      product: string;
      version: string;
    }
  | {
      kind: "styles";
      product: string;
      version: string;
    }
  | {
      kind: "layerSpecs";
      product: string;
      version: string;
      part?: string;
      element?: string;
      style?: string;
      layerName?: string;
    }
  | {
      kind: "shipping";
      product: string;
      version: string;
    }
  | {
      kind: "metadata";
      product: string;
      version: string;
    }
  | {
      kind: "global";
      product: string;
      version: string;
    }
  | {
      kind: "families";
      product?: string;
      version?: string;
      specName?: string;
    }
  | {
      kind: "fabrics";
      product?: string;
      version?: string;
      specName?: string;
    }
  | {
      kind: "decorations";
      product?: string;
      version?: string;
      specName?: string;
    }
  | {
      kind: "import";
      product?: string;
      version?: string;
    }
  | {
      kind: "settings";
      product?: string;
      version?: string;
    }
  | {
      kind: "history";
      product?: string;
      version?: string;
    }
  | {
      kind: "familiesHistory";
      product?: string;
      version?: string;
      specName?: string;
    }
  | {
      kind: "fabricsHistory";
      product?: string;
      version?: string;
      specName?: string;
    }
  | {
      kind: "decorationsHistory";
      product?: string;
      version?: string;
      specName?: string;
    };

// ── Editor runtime state ────────────────────────────────────────────

export interface SpecsEditorRuntimeState {
  /** Unique session id */
  id: string;
  /** Timestamp */
  createdAt: string;
  updatedAt: string;

  /** Parsed input file with layers (front view) */
  input: SpecsInput | null;
  /** Optional back-view input file */
  backInput: SpecsInput | null;

  /** Runtime state keyed by canonical model path. Specs structure stays in SpecsModel. */
  layerRuntimeByKey: Record<SpecsLayerRuntimeKey, SpecsLayerRuntimeState>;
  /** Runtime render order for model-backed layers. */
  layerRuntimeOrder: SpecsLayerRuntimeKey[];

  /** Base color for composite preview */
  previewColor: string;
  /** Reference image data URL for side-by-side comparison */
  referenceImageUrl: string | null;

  /** Global fal.ai generation settings */
  falSettings: {
    apiKey: string;
    resolution: string;
    aspectRatio: string;
    outputFormat: string;
  };

  // ── UI state (centralized so components are dumb readers) ──────────

  /** Canonical navigation state for the editor */
  nav: SpecsEditorNav;
  /** Global view mode for layer detail */
  layerView: SpecsLayerView;
  /** Layer being hovered in sidebar (for mask overlay) */
  hoveredLayerId: string | null;
  /** Active icon picker target */
  iconPickerTarget: SpecsEditorIconPickerTarget;
}

/** @deprecated Use SpecsEditorRuntimeState. */
export type SpecsEditorSession = SpecsEditorRuntimeState;

export type SpecsEditorIdentity = {
  productName: string;
  productDisplayName: string;
  productVersion: string;
};

// ── Raw spec types (from specs.json product data) ───────────────────

export interface RawSpecSide {
  name: string;
  zIndex?: number | null;
  zIndexHighlight?: number | null;
  imageUrl?: string | null;
  highlightUrl?: string | null;
  alwaysRedraw?: boolean;
}

export interface RawSpecLayer {
  name: string;
  layerLabel: string;
  fabricNames: string[];
  required: boolean;
  credits: number;
  parentOfInheritanceGroup?: string;
  excludeFromInheritance?: boolean;
  generator: { sides: RawSpecSide[] };
  display: {
    displayable: boolean;
    group: string;
    menuIconUrl?: string;
    displayMethod?: SpecsDisplayMethod;
  };
}

// ── Window augmentation for debugging ───────────────────────────────

declare global {
  interface Window {
    specs?: {
      readonly model?: SpecsModel;
      readonly snapshot?: ReturnType<SpecsModel["save"]>;
      readonly metadataModel?: unknown;
      readonly metadataSnapshot?: unknown;
    };
  }
}
